/**
 * R232q5s: Server-backed Quest Log — toggle J (Журнал поручений).
 *
 * Слушает server `quests-mine` RPC: активные квесты с прогрессом по
 * objective_kind + список completed (из listCompletedQuests). Каждый
 * квест clickable — раскрывает description, rewards, turn-in NPC.
 * При progress >= target_count — кнопка «Сдать квест» (без proximity-
 * проверки на этом этапе; server validate'ит).
 *
 * Не конфликтует с legacy QuestLog (L key) — different keybind, server-
 * authoritative track. Для гибридных систем оба могут жить.
 */

import { useEffect, useRef, useState } from 'react';
import { getRoom } from '../game/realmClient.js';

interface ActiveQuest {
  id: string;
  title: string;
  zone_id: string;
  zone_name: string;
  level_min: number;
  objective_text: string;
  objective_kind: string;
  target_count: number;
  progress: number;
  rewards: { xp: number; gold_aurei?: number; crystals?: number; faction_id?: string; faction_rep?: number };
  turnin_npc: string;
  completion_text: string;
}

const ZONE_LABELS_RU: Record<string, string> = {
  silver_fields: 'Серебряные Поля',
  blood_mountain: 'Кровогорье',
  ragged_sails_bay: 'Залив Рваных Парусов',
  lyov_glade: 'Лощина Светлых Ив',
  dusk_thicket: 'Сумеречные Чащи',
  coldridge: 'Хладногорье',
  ash_steppes: 'Ясеневые Степи',
  misty_cliffs: 'Туманные Утёсы',
  salt_mines: 'Соляные Копи',
  swamp_chambers: 'Болотные Чертоги',
  split_altar_ridge: 'Хребет Расколотого Алтаря',
  torn_veil: 'Прорванная Завеса',
};

const KIND_LABEL: Record<string, string> = {
  kill: 'Убить',
  kill_named: 'Сразить именного',
  gather: 'Собрать',
  deliver: 'Доставить',
  talk: 'Поговорить',
  escort: 'Сопроводить',
  discover: 'Разведать',
  scout: 'Разведать врагов',
  craft: 'Создать',
  dungeon: 'Пройти подземелье',
  arena_win: 'Победить на арене',
  faction_rep: 'Поднять Славу',
};

export function ServerQuestLog() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<ActiveQuest[]>([]);
  const [completed, setCompleted] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // R232q5ad: keep latest active quests + the in-flight turn-in target available
  // to async server callbacks without re-binding the message handler.
  const activeRef = useRef<ActiveQuest[]>([]);
  const pendingTurnInRef = useRef<ActiveQuest | null>(null);

  // J key toggles (Cyrillic О also). R232q5ay: also responds to MicroMenu's
  // L-button via the `aiwari-open-server-quest-log` custom event so legacy
  // L-binding from MicroMenu opens the server-driven log (legacy QuestLog
  // удалён в R232q5w, L-key биндился в null до этого фикса).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.key === 'j' || e.key === 'J' || e.key === 'о' || e.key === 'О') && !e.shiftKey && !e.ctrlKey) {
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen((o) => !o);
    window.addEventListener('keydown', onKey);
    window.addEventListener('aiwari-open-server-quest-log', onOpenEvent as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('aiwari-open-server-quest-log', onOpenEvent as EventListener);
    };
  }, [open]);

  // Server message subscriptions.
  useEffect(() => {
    const room = getRoom();
    if (!room) return;
    room.onMessage('quests-mine-result', (msg: { active: ActiveQuest[]; completed: string[] }) => {
      const list = msg.active ?? [];
      activeRef.current = list;
      setActive(list);
      setCompleted(msg.completed ?? []);
      setBusy(false);
    });
    room.onMessage('quest-turn-in-result', (msg: { ok: boolean; reason?: string; rewards?: ActiveQuest['rewards']; questId?: string }) => {
      setBusy(false);
      if (msg.ok) {
        setFlash('✓ Квест завершён');
        // R232q5ad: surface parchment-scroll reward modal. Fish title +
        // rewards from the active list before it gets refetched/cleared.
        const finished = pendingTurnInRef.current ?? activeRef.current.find((q) => q.id === msg.questId);
        if (finished) {
          window.dispatchEvent(new CustomEvent('aiwari-quest-complete', {
            detail: {
              questId: finished.id,
              title: finished.title,
              completionText: finished.completion_text,
              rewards: msg.rewards ?? finished.rewards,
            },
          }));
        } else if (msg.questId) {
          window.dispatchEvent(new CustomEvent('aiwari-quest-complete', {
            detail: { questId: msg.questId, rewards: msg.rewards },
          }));
        }
        pendingTurnInRef.current = null;
        room.send('quests-mine', {});
      } else {
        setFlash(`Не удалось сдать: ${turnInReasonRu(msg.reason)}`);
      }
    });
  }, []);

  // Fetch on open + 8s refresh while open.
  useEffect(() => {
    if (!open) return;
    const room = getRoom();
    if (!room) return;
    const fetch = () => {
      setBusy(true);
      room.send('quests-mine', {});
    };
    fetch();
    const iv = window.setInterval(fetch, 8000);
    return () => window.clearInterval(iv);
  }, [open]);

  // Auto-clear flash.
  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 3500);
    return () => window.clearTimeout(t);
  }, [flash]);

  const turnIn = (questId: string) => {
    const room = getRoom();
    if (!room) return;
    // R232q5ad: capture quest meta now so the reward modal can show title +
    // completion_text after server clears it from the active list.
    pendingTurnInRef.current = activeRef.current.find((q) => q.id === questId) ?? null;
    setBusy(true);
    room.send('quest-turn-in', { questId });
  };

  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center pointer-events-auto"
      style={{ background: 'radial-gradient(circle at center, rgba(11,9,7,0.78) 0%, rgba(0,0,0,0.92) 90%)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        className="relative max-w-[640px] w-[92vw] max-h-[88vh] flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
          border: '2px solid #5C4520',
          boxShadow: '0 0 60px rgba(201,169,106,0.3), inset 0 0 40px rgba(58,40,24,0.4)',
          fontFamily: 'EB Garamond, Georgia, serif',
          color: '#E8DCC0',
        }}
      >
        <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />

        {/* Header */}
        <div className="flex items-baseline justify-between px-6 pt-6 pb-3 border-b" style={{ borderColor: '#3A2818' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif' }}>Свиток поручений</div>
            <h2 className="text-[24px]" style={{ fontFamily: 'Cinzel, serif', color: '#C9A96A', fontWeight: 700 }}>
              Журнал поручений · {active.length} активных
            </h2>
          </div>
          <button onClick={() => setOpen(false)} className="text-[24px] hover:scale-110 transition" style={{ color: '#9C8E72' }}>×</button>
        </div>

        {flash && (
          <div className="px-6 pt-3">
            <div className="px-3 py-2 text-[12px] italic"
              style={{
                background: flash.startsWith('✓') ? 'rgba(91,174,87,0.16)' : 'rgba(163,50,50,0.16)',
                color: flash.startsWith('✓') ? '#5BAE57' : '#A33232',
                border: `1px solid ${flash.startsWith('✓') ? '#5BAE57' : '#A33232'}`,
              }}>
              {flash}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {active.length === 0 ? (
            <div className="text-[13px] italic text-center py-8" style={{ color: '#9C8E72' }}>
              {busy ? 'Свитки разворачиваются…' : 'Нет активных квестов. Подойди к NPC с восклицательным знаком над головой и нажми F.'}
            </div>
          ) : (
            <div className="space-y-2">
              {active.map((q) => {
                const isExpanded = expanded === q.id;
                const isComplete = q.progress >= q.target_count;
                const pct = Math.min(100, Math.round((q.progress / Math.max(1, q.target_count)) * 100));
                return (
                  <div
                    key={q.id}
                    className="cursor-pointer transition"
                    style={{
                      background: isComplete ? 'rgba(91,174,87,0.14)' : 'rgba(58,40,24,0.3)',
                      border: `1px solid ${isComplete ? '#5BAE57' : '#3A2818'}`,
                    }}
                    onClick={() => setExpanded(isExpanded ? null : q.id)}
                  >
                    <div className="px-3 py-2 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="truncate" style={{ fontFamily: 'Cinzel, serif', fontWeight: 600, color: isComplete ? '#5BAE57' : '#C9A96A' }}>
                          {isComplete ? '✓ ' : ''}{q.title}
                        </div>
                        <div className="text-[10px]" style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif', letterSpacing: '0.1em' }}>
                          {ZONE_LABELS_RU[q.zone_id] ?? q.zone_id} · ур. {q.level_min}+
                        </div>
                      </div>
                      <div className="text-right text-[12px]" style={{ fontFamily: 'JetBrains Mono, monospace', color: isComplete ? '#5BAE57' : '#E8C77B' }}>
                        {q.progress} / {q.target_count}
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ height: 2, background: '#3A2818' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: isComplete ? '#5BAE57' : '#C9A96A', transition: 'width 300ms ease' }} />
                    </div>

                    {isExpanded && (
                      <div className="px-3 pb-3 pt-2 border-t" style={{ borderColor: '#3A2818' }}>
                        <div className="text-[12px] italic" style={{ color: '#E8DCC0' }}>
                          <span style={{ color: '#8A6B2E' }}>Цель: </span>
                          <span style={{ color: '#9C8E72' }}>[{KIND_LABEL[q.objective_kind] ?? q.objective_kind}]</span> {q.objective_text}
                        </div>
                        <div className="text-[10px] mt-2" style={{ color: '#9C8E72' }}>
                          Сдать у: <span style={{ color: '#C9A96A' }}>{q.turnin_npc}</span>
                        </div>
                        <div className="text-[10px] mt-1 flex flex-wrap gap-2" style={{ color: '#9C8E72' }}>
                          <span>Награда:</span>
                          {q.rewards.xp > 0 && <span style={{ color: '#E8C77B' }}>{q.rewards.xp} опыта</span>}
                          {q.rewards.gold_aurei && q.rewards.gold_aurei > 0 && <span style={{ color: '#C9A96A' }}>{q.rewards.gold_aurei} ауреев</span>}
                          {q.rewards.faction_rep && q.rewards.faction_rep > 0 && <span style={{ color: '#5BAE57' }}>+{q.rewards.faction_rep} славы</span>}
                          {q.rewards.crystals && q.rewards.crystals > 0 && <span style={{ color: '#3E6FA5' }}>{q.rewards.crystals} кристаллов</span>}
                        </div>
                        {isComplete && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); turnIn(q.id); }}
                            disabled={busy}
                            className="mt-3 px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] hover:scale-[1.03] transition"
                            style={{
                              background: 'linear-gradient(180deg, #5BAE57, #3F7E3D)',
                              color: '#0B0907',
                              border: '2px solid #3A2818',
                              fontFamily: 'Cinzel, serif',
                              fontWeight: 600,
                              cursor: busy ? 'wait' : 'pointer',
                            }}
                          >
                            Сдать квест
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {completed.length > 0 && (
            <div className="mt-6 pt-4 border-t" style={{ borderColor: '#3A2818' }}>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9C8E72', fontFamily: 'Spectral SC, serif' }}>
                Завершено всего: <span style={{ color: '#5BAE57' }}>{completed.length}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-2 text-[10px] uppercase tracking-[0.3em] text-center" style={{ background: 'rgba(58,40,24,0.4)', borderTop: '1px solid #3A2818', fontFamily: 'Spectral SC, serif', color: '#9C8E72' }}>
          J · Esc · server-authoritative · 8с автообновление
        </div>
      </div>
    </div>
  );
}

function turnInReasonRu(reason?: string): string {
  switch (reason) {
    case 'unknown': return 'квест неизвестен';
    case 'not-accepted': return 'квест не принят';
    case 'objective-incomplete': return 'цель не выполнена';
    case 'no-character': return 'нет персонажа';
    case 'db-error': return 'ошибка базы';
    default: return reason ?? 'неизвестная причина';
  }
}

function Corner({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const cls: Record<typeof pos, string> = {
    tl: 'top-2 left-2',
    tr: 'top-2 right-2 scale-x-[-1]',
    bl: 'bottom-2 left-2 scale-y-[-1]',
    br: 'bottom-2 right-2 scale-x-[-1] scale-y-[-1]',
  };
  return (
    <svg
      className={`absolute ${cls[pos]} w-7 h-7 pointer-events-none`}
      viewBox="0 0 28 28"
      fill="none"
      stroke="#5C4520"
      strokeWidth="1.5"
    >
      <path d="M2 2 L10 2 M2 2 L2 10" />
      <path d="M3 3 Q9 3 9 9" />
      <circle cx="5" cy="5" r="1" fill="#5C4520" />
    </svg>
  );
}
